<template>
  <div>
    <div id="releaseNoteView">
      <v-card class="och-releaseNoteView elevation-0">
        <v-row>
          <v-col cols="12" md="8" class="och-border--right">
            <v-card class="elevation-0" v-if="releaseNote.updatedReason">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">Update</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.updatedReason"
                  data-cy="releaseNoteView__text--updatedReason"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.summary">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">Summary</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html :value="releaseNote.summary" data-cy="releaseNoteView__text--summary"></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.preGoLiveSetup">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">Go-Live Setup</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.preGoLiveSetup"
                  data-cy="releaseNoteView__text--preGoLiveSetup"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.background">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">Background</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.background"
                  data-cy="releaseNoteView__text--background"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.issue">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">Issue</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html :value="releaseNote.issue" data-cy="releaseNoteView__text--issue"></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.currentBehavior">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">Current Behavior</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.currentBehavior"
                  data-cy="releaseNoteView__text--currentBehavior"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.newBehavior">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">New Behavior</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.newBehavior"
                  data-cy="releaseNoteView__text--newBehavior"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.optionalEnhancement">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1"
                >Optional Enhancement</v-card-title
              >
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.optionalEnhancement"
                  data-cy="releaseNoteView__text--optionalEnhancement"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.note">
              <v-card-title primary-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1"
                >Note</v-card-title
              >
              <v-card-text class="pt-1">
                <embedded-html :value="releaseNote.note" data-cy="releaseNoteView__text--note"></embedded-html>
              </v-card-text>
            </v-card>

            <v-card class="elevation-0" v-if="releaseNote.requiredSetup && releaseNote.templateType === 'rte'">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n2 subtitle-1">UAT Purpose</v-card-title>
              <v-card-text class="pt-1">
                <embedded-html
                  :value="releaseNote.requiredSetup"
                  data-cy="releaseNoteView__text--requiredSetup"
                ></embedded-html>
              </v-card-text>
            </v-card>

            <test-plan-comments
              v-if="!isOchinUser"
              :releaseNoteId="releaseNoteId"
              :editable="hasTestPlanEdit"
            ></test-plan-comments>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="pb-4 elevation-0">
              <v-card-title class="primary--text font-weight-bold pb-0 mb-n3 subtitle-1"
                >Release Note Details</v-card-title
              >
              <v-list dense>
                <v-list-item v-if="releaseNote.application">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                      >Application</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-text="releaseNote.application"
                      data-cy="releaseNoteView__text--module"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="releaseNote.primaryActivity">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                      >Primary Activity</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-text="releaseNote.primaryActivity"
                      data-cy="releaseNoteView__text--primaryActivity"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="releaseNote.activitiesImpacted">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                      >Other Activities Impacted</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-text="releaseNote.activitiesImpacted"
                      data-cy="releaseNoteView__text--otherActivitiesImpacted"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="releaseNote.operationalizeType">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                      >Operationalize Type</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-text="releaseNote.operationalizeType"
                      data-cy="releaseNoteView__text--operationalizeType"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="releaseNote.buildType">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3">Build Type</v-list-item-title>
                    <v-list-item-subtitle
                      v-text="releaseNote.buildType"
                      data-cy="releaseNoteView__text--buildType"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="releaseNote.releaseType">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                      >Release Type</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-text="releaseNote.releaseType"
                      data-cy="releaseNoteView__text--releaseType"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="Array.isArray(releaseNote.userRole) && releaseNote.userRole.length">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3">User Role</v-list-item-title>
                    <div
                      class="och-v-list-item__subtitle--custom"
                      v-text="releaseNote.userRole.join(', ')"
                      data-cy="releaseNoteView__text--userRole"
                    ></div>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                      >Care Setting</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-text="releaseNote.careSetting"
                      data-cy="releaseNoteView__text--careSetting"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <div v-if="showServiceAreaAttributeList">
                <v-divider class="mt-4"></v-divider>
                <v-card-title class="primary--text font-weight-bold pb-0 mb-n3 subtitle-1">Impacts</v-card-title>
                <v-card-text class="pa-0 ma-0" data-cy="releaseNoteView__text--serviceAreaAttributeList">
                  <v-list dense class="mb-4">
                    <v-list-item class="mb-n4" v-for="(item, index) in serviceAreaAttributeList" :key="index">
                      <v-list-item-content>
                        <v-list-item-title v-if="item">
                          {{ item.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </div>
              <v-divider></v-divider>

              <v-card-title class="primary--text font-weight-bold pb-0 mb-n3 subtitle-1">OCHIN Staff</v-card-title>
              <v-card-text class="pa-0 ma-0">
                <v-list dense>
                  <v-list-item v-if="releaseNote.buildAnalyst">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                        >Build Analyst</v-list-item-title
                      >
                      <v-list-item-subtitle
                        v-text="releaseNote.buildAnalystDisplayName || '--'"
                        data-cy="releaseNoteView__text--buildAnalyst"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="releaseNote.qaAnalyst">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                        >QA Analyst</v-list-item-title
                      >
                      <v-list-item-subtitle
                        v-text="releaseNote.qaAnalystDisplayName || '--'"
                        data-cy="releaseNoteView__text--qaAnalyst"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="releaseNote.jiraSupportAnalyst">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold grey--text text--darken-3"
                        >JIRA Support Analyst</v-list-item-title
                      >
                      <v-list-item-subtitle data-cy="releaseNoteView__text--jiraSupportAnalyst">
                        <ul v-for="jiraSupportAnalyst in releaseNote.jiraSupportAnalyst">
                          <li v-text="formatUsername(jiraSupportAnalyst)"></li>
                        </ul>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider class="mt-4"></v-divider>

                  <v-card-title class="primary--text font-weight-bold pb-0 mb-n3 subtitle-1">JIRA</v-card-title>
                  <v-list-item class="mb-n4" v-if="isOchinUser">
                    <v-list-item-content data-cy="releaseNoteView__text--buildJira">
                      <v-list-item-title>
                        <label class="font-weight-bold grey--text text--darken-3 mr-1">Build JIRA:</label>
                        <span v-for="(jira, index) in buildJiras" :key="index">
                          <template v-if="isJiraKey(jira)">
                            <a :href="`https://jira.ochin.org/browse/${jira}`" target="_blank" rel="noopener noreferrer">
                              <span v-text="jira"></span>
                            </a>
                          </template>
                          <template v-else>
                            <span v-text="jira"></span>
                          </template>
                          <span v-if="index < buildJiras.length - 1" class="mr-1">,</span>
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="mb-n4" v-if="isOchinUser">
                    <v-list-item-content data-cy="releaseNoteView__text--qaJira">
                      <v-list-item-title>
                        <label class="font-weight-bold grey--text text--darken-3 mr-1">QA JIRA:</label>
                        <span v-for="(jira, index) in qaJiras" :key="jira">
                          <a :href="`https://jira.ochin.org/browse/${jira}`" target="_blank" rel="noopener noreferrer">
                            <span v-text="jira"></span>
                          </a>
                          <span v-if="index < qaJiras.length - 1">,</span>
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="mb-n4" v-if="releaseNote.uatJira">
                    <v-list-item-title>
                      <label class="mr-1">
                        <strong>UAT JIRA:</strong>
                      </label>
                      <a :href="`https://jira.ochin.org/browse/${releaseNote.uatJira}`" target="_blank" rel="noopener noreferrer">
                        <span v-text="releaseNote.uatJira" data-cy="releaseNoteView__text--uatJira"></span>
                      </a>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="!releaseNote.uatJira && permissions.releaseNoteJiraCreate && isOchinUser"
                    class="mt-3"
                  >
                    <v-list-item-content>
                      <div>
                        <v-btn small class="mx-0 elevation-0" @click="createJira('uat')" :disabled="isCreatingJira">
                          <v-icon small class="grey--text text--darken-2 mr-2" v-if="!this.isCreatingJira"
                            >fab fa-jira</v-icon
                          >
                          <v-icon small class="grey--text text--darken-2 mr-2" v-if="this.isCreatingJira"
                            >fas fa-cog fa-spin</v-icon
                          >Create UAT JIRA
                        </v-btn>
                      </div>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item class="mb-n4" v-if="releaseNote.trainingJira && isOchinUser">
                    <v-list-item-title>
                      <label class="mr-1">
                        <strong>Training JIRA:</strong>
                      </label>
                      <template v-if="isJiraKey(releaseNote.trainingJira)">
                        <a :href="`https://jira.ochin.org/browse/${releaseNote.trainingJira}`" target="_blank" rel="noopener noreferrer">
                          <span v-text="releaseNote.trainingJira" data-cy="releaseNoteView__text--trainingJira"></span>
                        </a>
                      </template>
                      <template v-else>
                        <span v-text="releaseNote.trainingJira" data-cy="releaseNoteView__text--trainingJira"></span>
                      </template>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!releaseNote.trainingJira && isOchinUser && permissions.releaseNoteJiraCreate"
                    class="mt-2"
                  >
                    <v-list-item-content>
                      <div>
                        <v-btn
                          small
                          class="mx-0 elevation-0"
                          @click="createJira('training')"
                          :disabled="isCreatingJira"
                        >
                          <v-icon small class="grey--text text--darken-2 mr-2" v-if="!this.isCreatingJira"
                            >fab fa-jira</v-icon
                          >
                          <v-icon small class="grey--text text--darken-2 mr-2" v-if="this.isCreatingJira"
                            >fas fa-cog fa-spin</v-icon
                          >Create Training JIRA
                        </v-btn>
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </div>
    <v-dialog v-model="dialog" max-width="90%" width="auto" persistent>
      <v-card>
        <v-card-text class="text-sm-center">
          <img class="och-border--all och-img--dialog" :src="dialogImage" @click="dialog = false" />
          <div class="mt-4">
            <v-pagination
              :circle="true"
              color="primary"
              :length="imageCatalog.length"
              :total-visible="imageCatalog.length"
              v-model="currentImageIdx"
            ></v-pagination>
          </div>
          <div class="text-sm-center">
            <v-btn color="primary darken-1" :text="true" @click="dialog = false">
              <v-icon class="mr-2" small>fas fa-times</v-icon>Close
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatUsername, isJiraKey } from "@/lib/formatters";
import { mapActions, mapGetters, mapState } from "vuex";
import EmbeddedHtml from "@/components/embeddedHtml";
import jiraApi from "@/api/jira";
import TestPlanComments from "@/components/testPlanComments";

export default {
  name: "releaseNoteView",
  components: {
    EmbeddedHtml,
    TestPlanComments,
  },
  computed: {
    buildJiras() {
      const { buildJira } = this.releaseNote;
      if (buildJira) {
        return buildJira.split(",").map(jira => jira.trim()).filter(Boolean);
      }
      return [];
    },
    permissions() {
      return this.$store.state.session.user.permissions;
    },
    qaJiras() {
      const { qaJira } = this.releaseNote;
      if (qaJira) {
        return qaJira.split(",").map(jira => jira.trim());
      }
      return [];
    },
    releaseNote() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    serviceAreaAttributeList() {
      const serviceAreaAttributeList = this.releaseNote.serviceAreaAttributeList.map(attributeId => {
        const attribute = this.getServiceAreaAttribute(attributeId);
        return attribute;
      });
      return serviceAreaAttributeList;
    },
    showServiceAreaAttributeList() {
      return (
        Array.isArray(this.releaseNote.serviceAreaAttributeList) &&
        this.releaseNote.serviceAreaAttributeList.length &&
        Array.isArray(this.serviceAreaAttributeList) &&
        this.serviceAreaAttributeList.length
      );
    },
    isOchinUser() {
      return this.$store.state.session.user.isOchinUser || false;
    },
    ...mapGetters({
      getServiceAreaAttribute: "admin/serviceAreaAttribute/getById",
      hasTestPlanEdit: "session/hasTestPlanEdit",
      userMap: "user/getOchinUserList",
    }),
    ...mapState({
      userList: state => state.user.userList,
    }),
  },
  async created() {
    try {
      await Promise.all([this.fetchServiceAreaAttributeList(), this.fetchUserList()]);
    } catch (e) {
      console.error(e);
    }
  },
  data() {
    return {
      currentImageIdx: 1,
      dialog: false,
      dialogImage: "",
      imageCatalog: [],
      isCreatingJira: false,
    };
  },
  methods: {
    isJiraKey,

    async createJira(jiraType) {
      this.isCreatingJira = true;
      try {
        await jiraApi.create({
          jiraType,
          releaseNoteId: this.releaseNoteId,
        });
        await this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId);
      } catch (e) {
        console.error(e);
        const msg = e.response?.data?.message;
        const message =
          typeof msg === "string"
            ? msg
            : [msg?.reporter, msg?.assignee].filter(Boolean).join("\n") || "Could not create JIRA";
        this.$notifyError({ message, timeout: 0 });
      } finally {
        this.isCreatingJira = false;
      }
    },
    formatUsername(username) {
      return formatUsername(username, this.userMap);
    },
    loadImage(dialogImage) {
      this.currentImageIdx = this.imageCatalog.indexOf(dialogImage) + 1;
      this.dialogImage = dialogImage;
      this.dialog = true;
    },
    ...mapActions({
      fetchServiceAreaAttributeList: "admin/serviceAreaAttribute/list",
      fetchUserList: "user/loadOchinUsers",
    }),
  },
  props: ["packId", "releaseNoteId"],
  watch: {
    currentImageIdx(val) {
      this.dialogImage = this.imageCatalog[val - 1];
    },
  },
};
</script>

<style>
.och-border--right {
  border-right: 1px solid #cccccc;
}

.och-v-list-item__subtitle--custom {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1rem;
}

.och-releaseNoteView p:last-child {
  margin-bottom: -1rem;
}

.och-releaseNoteView .v-list-item__subtitle {
  margin-bottom: -0.3rem;
}

.och-img--dialog img {
  max-width: 100%;
}

#releaseNoteView img {
  cursor: pointer;
}
</style>
